import { useState, useEffect, Suspense, lazy } from 'react';
import Layout from 'Component/Layouts/layout';
import { Box, Typography, Tab, Tabs, CircularProgress } from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import useApiCaller from 'hooks/apiCaller';
import useCredentials from 'hooks/useCredentials';
import { headerSx } from './styles';

// Each tab is code-split into its own chunk and loaded on demand, so opening the
// DYK route no longer ships every tab (and its dialogs) in the initial bundle.
const RepositoryTab = lazy(() => import('./components/RepositoryTab'));
const CircleConfigTab = lazy(() => import('./components/CircleConfigTab'));
const ContentListTab = lazy(() => import('./components/ContentListTab'));
const ReportsDashboardTab = lazy(() => import('./components/ReportsDashboardTab'));
const WinnersTab = lazy(() => import('./components/WinnersTab'));

const DYKSection = () => {
  const apiCaller = useApiCaller();
  const { userData: user } = useCredentials();
  const isAdmin = user?.roles?.includes('admin');

  // Winner-tab access is authoritative from the backend token roles + regional-admin lookup.
  const [winnerAccess, setWinnerAccess] = useState({ isSuperAdmin: false, isRegionalAdmin: false });

  useEffect(() => {
    apiCaller
      .get('/super-user/dyk/winners/access')
      .then((res) => setWinnerAccess(res.data?.data || {}))
      .catch(() => {});
  }, []);

  const showWinners = winnerAccess.isSuperAdmin || winnerAccess.isRegionalAdmin;

  // Tab indices differ by role:
  // Normal user: tab 0 = Repository only (+ Winners if regional admin)
  // Admin:       tab 0 = Repository, 1 = Circle Config, 2 = DYK Content, 3 = Reports, 4 = Winners
  const [tab, setTab] = useState(0);

  const adminTabs = [
    { label: 'DYK Library', component: <RepositoryTab apiCaller={apiCaller} /> },
    { label: 'Circle Configuration', component: <CircleConfigTab apiCaller={apiCaller} /> },
    { label: 'DYK Content', component: <ContentListTab apiCaller={apiCaller} /> },
    { label: 'Reports & Dashboard', component: <ReportsDashboardTab apiCaller={apiCaller} /> },
  ];
  const userTabs = [{ label: 'DYK Library', component: <RepositoryTab apiCaller={apiCaller} /> }];
  const tabs = [...(isAdmin ? adminTabs : userTabs)];
  if (showWinners) {
    tabs.push({
      label: 'Winners',
      component: <WinnersTab apiCaller={apiCaller} isSuperAdmin={!!winnerAccess.isSuperAdmin} />,
    });
  }

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        <Box sx={headerSx}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <LightbulbIcon sx={{ color: '#6366f1', fontSize: 28 }} />
            <Box>
              <Typography variant="h5" fontWeight="700" color="#1e293b">
                DYK Module
              </Typography>
              <Typography variant="body2" color="#64748b">
                Did You Know — content library &amp; questionnaires
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', mb: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              px: 2,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' },
            }}
          >
            {tabs.map((t, i) => (
              <Tab key={i} label={t.label} />
            ))}
          </Tabs>
        </Box>

        <Suspense
          fallback={
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          }
        >
          {tabs[tab]?.component}
        </Suspense>
      </Box>
    </Layout>
  );
};

export default DYKSection;
